import React, { useCallback, useState } from 'react';
import { Button, Space, Collapse, Checkbox } from 'antd';
import { CaretRightOutlined, DeleteOutlined } from '@ant-design/icons';
import { useField, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import {
  confirmation,
  DeleteIconButton,
  Form,
  SELECT_OTHER_VALUE_PREFIX,
} from '@/components';
import styles from '../FormPathTemplate.module.scss';
import classNames from 'classnames';
import { HeaderActions } from './TasksHeader';
import { GroupDropWrapper, DraggableTask } from './GroupDropWrapper';
import { MAX_VALUE } from '@/views/Tasks.Details';
import {
  PathTemplateTaskDto,
  PathTemplateTaskGroupDto,
  TaskResponsiblePersonValue,
} from '@/core';
import i18next from 'i18next';
import { ResponsiblePersonInput } from '@/views/Tasks.Details/ImportTasks/Fields/ResponsiblePersonInput';
import { get } from 'lodash';

const { Panel } = Collapse;

const DEFAULT_GROUP_NAME = 'New Group';
const TEMP_ID_PREFIX = 'TempId://';

const generateId = () =>
  TEMP_ID_PREFIX + Math.random().toString(36).substring(2, 9);

export interface GroupedTaskDataFormValues {
  groups: PathTemplateTaskGroupDto[];
  tasks: PathTemplateTaskDto[];
}

export const groupedTaskDataValidationSchema = Yup.object().shape({
  groups: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().nullable().required(),
    }),
  ),
  tasks: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().nullable().required(),
      costEstimate: Yup.number().min(0).max(MAX_VALUE).nullable().optional(),
      responsiblePerson: Yup.string()
        .test(
          'has-value',
          i18next.t('errors.mixed.required'),
          function (value) {
            if (value == null) return true;

            if (value && value.startsWith(SELECT_OTHER_VALUE_PREFIX)) {
              const suffix = value
                .substring(SELECT_OTHER_VALUE_PREFIX.length)
                .trim();
              return suffix.length > 0;
            } else {
              return !!value ? value.length > 0 : false;
            }
          },
        )
        .nullable(),
    }),
  ),
});

export const GROUPED_TASKS_DATA_FORM_INITIAL_VALUE: GroupedTaskDataFormValues =
  {
    groups: [],
    tasks: [],
  };

interface Props {
  name: string;
  communityId: string | undefined;
}

export const FormGroupedTaskData: React.FC<Props> = (props) => {
  const { name, communityId } = props;
  const [addClicked, setAddClicked] = useState<boolean>(false);

  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(
    new Set(),
  );

  const { initialValues } = useFormikContext<{
    [key: string]: GroupedTaskDataFormValues;
  }>();

  const [{ value: values }, , { setValue: setFieldValue }] =
    useField<GroupedTaskDataFormValues>(name);

  const handleImport = useCallback(
    ({ groups, tasks }: GroupedTaskDataFormValues) => {
      setFieldValue({
        ...values,
        groups: [...values.groups, ...groups],
        tasks: [...values.tasks, ...tasks],
      });
    },
    [values, setFieldValue],
  );

  const { t } = useTranslation(undefined, {
    keyPrefix: 'pathTemplates.details.groupTasks',
  });

  const toggleSelection = (index: number) => {
    setSelectedIndexes((prev) => {
      const newSet = new Set(prev);
      newSet.has(index) ? newSet.delete(index) : newSet.add(index);
      return newSet;
    });
  };

  if (values.tasks.length < 1 && values.groups.length < 1 && !addClicked)
    return (
      <Button className={styles.stepButton} onClick={() => setAddClicked(true)}>
        {t('addTasks')}
      </Button>
    );

  return (
    <DndProvider backend={HTML5Backend}>
      <HeaderActions
        community={communityId}
        onAddTask={() => {
          const newTask = {
            name: null!,
            costEstimate: undefined,
            groupId: null,
          };
          setFieldValue({
            ...values,
            tasks: [...values.tasks, newTask],
          });
        }}
        onGroupTasks={() => {
          if (selectedIndexes.size === 0) return;
          const newGroupId = generateId();
          const updatedTasks = values.tasks.map((t, i) =>
            selectedIndexes.has(i) ? { ...t, groupId: newGroupId } : t,
          );
          setFieldValue({
            ...values,
            groups: [
              ...values.groups,
              { id: newGroupId, name: DEFAULT_GROUP_NAME },
            ],
            tasks: updatedTasks,
          });
          setSelectedIndexes(new Set());
        }}
        onImport={handleImport}
        groupEnable={selectedIndexes.size > 0}
      />

      {values?.groups?.map((group, index) => (
        <GroupDropWrapper
          key={group.id}
          groupId={group.id}
          onDrop={(taskIndex, groupId) => {
            const updatedTasks = [...values.tasks];
            updatedTasks[taskIndex].groupId = groupId;
            setFieldValue({ ...values, tasks: updatedTasks });
          }}
        >
          <Collapse
            bordered={false}
            className={classNames(styles.collapse, 'mb-1')}
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
          >
            <Panel
              header={
                <Form.Input
                  name={`${name}.groups[${index}].name`}
                  label={false}
                  mode="always"
                />
              }
              key={group.id}
              extra={
                <Button
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmation.show({
                      title: t('removeGroup.title'),
                      body: t('removeGroup.body'),
                      onConfirm: () => {
                        const updatedTasks = values.tasks.map((t) =>
                          t.groupId === group.id ? { ...t, groupId: null } : t,
                        );
                        const updatedGroups = values.groups.filter(
                          (g) => g.id !== group.id,
                        );
                        setFieldValue({
                          ...values,
                          groups: updatedGroups,
                          tasks: updatedTasks,
                        });
                      },
                    });
                  }}
                >
                  {t('removeGroup.title')}
                </Button>
              }
            >
              {values.tasks.map(
                (task, index) =>
                  task.groupId === group.id && (
                    <Space
                      key={index}
                      className="mb-1 w-100"
                      direction="horizontal"
                    >
                      <Form.Input
                        placeholder={t('tasks.name')}
                        name={`${name}.tasks[${index}].name`}
                        label={false}
                        mode="always"
                      />
                      <ResponsiblePersonInput
                        className={styles.asyncSelect}
                        communityId={communityId}
                        name={`${name}.tasks[${index}]`}
                        placeholder={t('tasks.responsiblePerson')}
                        initialValue={
                          get(
                            initialValues,
                            `${name}.tasks[${index}].responsiblePerson`,
                          ) as TaskResponsiblePersonValue
                        }
                      />
                      <Form.Money
                        placeholder={t('tasks.cost')}
                        name={`${name}.tasks[${index}].costEstimate`}
                        label={false}
                        min={0}
                      />
                      <Button
                        className={styles.taskAction}
                        type="link"
                        onClick={() => {
                          const updatedTasks = [...values.tasks];
                          const removedGroupId = updatedTasks[index].groupId;
                          updatedTasks[index] = {
                            ...updatedTasks[index],
                            groupId: null,
                          };

                          const stillUsed = updatedTasks.some(
                            (task) => task.groupId === removedGroupId,
                          );

                          const updatedGroups = stillUsed
                            ? values.groups
                            : values.groups.filter(
                                (g) => g.id !== removedGroupId,
                              );
                          setFieldValue({
                            ...values,
                            tasks: updatedTasks,
                            groups: updatedGroups,
                          });
                        }}
                      >
                        {t('ungroup')}
                      </Button>
                    </Space>
                  ),
              )}
            </Panel>
          </Collapse>
        </GroupDropWrapper>
      ))}

      <br />

      {values?.tasks?.map(
        (task, index) =>
          task.groupId == null && (
            <DraggableTask key={index} index={index}>
              <Space className={classNames(styles.taskRow)}>
                <Checkbox
                  checked={selectedIndexes.has(index)}
                  onChange={() => toggleSelection(index)}
                />
                <Form.Input
                  placeholder={t('tasks.name')}
                  name={`${name}.tasks[${index}].name`}
                  label={false}
                  mode="always"
                />
                <ResponsiblePersonInput
                  placeholder={t('tasks.responsiblePerson')}
                  className={styles.asyncSelect}
                  communityId={communityId}
                  name={`${name}.tasks[${index}]`}
                  initialValue={
                    get(
                      initialValues,
                      `${name}.tasks[${index}].responsiblePerson`,
                    ) as TaskResponsiblePersonValue
                  }
                />
                <Form.Money
                  placeholder={t('tasks.cost')}
                  label={false}
                  name={`${name}.tasks[${index}].costEstimate`}
                  min={0}
                />

                <DeleteIconButton
                  onClick={() => {
                    const updated = values.tasks.filter((_, i) => i !== index);
                    setFieldValue({ ...values, tasks: updated });
                  }}
                />
              </Space>
            </DraggableTask>
          ),
      )}
    </DndProvider>
  );
};
